import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Switch,
  Chip,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from '@tanstack/react-query';
import MobileDialog from './MobileDialog';
import {
  createCashDesk,
  updateCashDesk,
  deleteCashDesk,
} from '../../services/cashflowService';

const ManageCashDesksDialog = ({
  open,
  onClose,
  cashDesks = [],
  refetchCashDesks,
}) => {
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [localError, setLocalError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const queryClient = useQueryClient();

  const handleAdd = async () => {
    if (!newName.trim()) {
      setLocalError('שם הקופה הוא שדה חובה');
      return;
    }
    setLocalError('');
    setIsAdding(true);
    try {
      await createCashDesk({ Name: newName.trim(), Description: newDescription.trim() });
      setNewName('');
      setNewDescription('');
      refetchCashDesks();
      queryClient.refetchQueries({ queryKey: ['cash-desks'] });
    } catch (err) {
      setLocalError(err?.response?.data?.error || 'שגיאה ביצירת קופה');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleActive = async (cashDesk) => {
    setLocalError('');
    try {
      await updateCashDesk({
        CashDeskID: cashDesk.CashDeskID,
        IsActive: !cashDesk.IsActive,
      });
      refetchCashDesks();
    } catch (err) {
      setLocalError(err?.response?.data?.error || 'שגיאה בעדכון קופה');
    }
  };

  const handleStartEdit = (cashDesk) => {
    setEditingId(cashDesk.CashDeskID);
    setEditName(cashDesk.Name);
    setEditDescription(cashDesk.Description || '');
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      setLocalError('שם הקופה הוא שדה חובה');
      return;
    }
    setLocalError('');
    try {
      await updateCashDesk({
        CashDeskID: editingId,
        Name: editName.trim(),
        Description: editDescription.trim(),
      });
      setEditingId(null);
      refetchCashDesks();
    } catch (err) {
      setLocalError(err?.response?.data?.error || 'שגיאה בעדכון קופה');
    }
  };

  const handleDelete = async (cashDeskId) => {
    setLocalError('');
    try {
      await deleteCashDesk(cashDeskId);
      refetchCashDesks();
      queryClient.refetchQueries({ queryKey: ['cash-desks'] });
    } catch (err) {
      setLocalError(err?.response?.data?.error || 'שגיאה במחיקת קופה');
    }
  };

  return (
    <MobileDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.4rem' }}>
        ניהול קופות
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localError && (
            <Alert severity="error" onClose={() => setLocalError('')}>
              {localError}
            </Alert>
          )}

          {/* Add new cash desk */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label="שם קופה חדשה"
              fullWidth
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
            <TextField
              label="תיאור (אופציונלי)"
              fullWidth
              size="small"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={isAdding || !newName.trim()}
              fullWidth
              sx={{ direction: 'ltr' }}
            >
              {isAdding ? <CircularProgress size={20} /> : 'הוסף'}
            </Button>
          </Box>

          <Divider />

          {/* Cash desk list */}
          {cashDesks.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
              אין קופות. הוסף קופה חדשה למעלה.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {cashDesks.map((cd) => (
                <Box
                  key={cd.CashDeskID}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: cd.IsActive ? 'transparent' : 'action.hover',
                    opacity: cd.IsActive ? 1 : 0.6,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {editingId === cd.CashDeskID ? (
                    <>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="תיאור"
                        />
                      </Box>
                      <IconButton size="small" color="success" onClick={handleSaveEdit}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setEditingId(null)}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 500 }}>{cd.Name}</Typography>
                          {!cd.IsActive && (
                            <Chip label="לא פעילה" size="small" color="default" />
                          )}
                        </Box>
                        {cd.Description && (
                          <Typography variant="body2" color="text.secondary">
                            {cd.Description}
                          </Typography>
                        )}
                      </Box>
                      <Switch
                        checked={cd.IsActive}
                        onChange={() => handleToggleActive(cd)}
                        size="small"
                        title={cd.IsActive ? 'השבת' : 'הפעל'}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleStartEdit(cd)}
                        title="ערוך"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(cd.CashDeskID)}
                        title="מחק"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="error" size="large" sx={{ fontSize: '1rem' }}>
          סגור
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default ManageCashDesksDialog;
